// Do not change this file, it is managed by the original file in the backend repo `jonni-backend/shared`
import { bugsnag } from '@jonni/services/BugsnagService';
import { isEnvLocalDev } from '@jonni/shared/utils/envUtils';

/**
 * Types for the logger service
 */
type MetaData = Record<string, unknown>;
type Severity = 'info' | 'warning' | 'error';

/**
 * Formats a value for string representation
 * @param val - The value to format
 * @param seen - Set of seen objects for circular reference detection
 * @param indent - Current indentation level
 * @returns A string representation of the value
 */
const format = (val: unknown, seen = new WeakSet(), indent = 0): string => {
  const spaces = ' '.repeat(indent);

  // Handle primitives and special cases
  if (val === null) return 'null';
  if (val === undefined) return 'undefined';
  if (typeof val === 'string') return `'${val}'`;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'function') return `[Function${val.name ? ': ' + val.name : ''}]`;
  if (typeof val === 'symbol') return val.toString();
  if (val instanceof Date) return val.toISOString();
  if (val instanceof Error) return `[${val.name}: ${val.message}]`;

  // Handle circular references
  if (typeof val === 'object') {
    if (seen.has(val)) return '[Circular]';
    seen.add(val);

    if (Array.isArray(val)) {
      if (val.length === 0) return '[]';
      const items = val.map((v) => format(v, seen, indent + 2)).join(',\n' + spaces + '  ');
      return `[\n${spaces}  ${items}\n${spaces}]`;
    }

    const entries = Object.entries(val);
    if (entries.length === 0) return '{}';

    const props = entries
      .map(([key, value]) => `${key}: ${format(value, seen, indent + 2)}`)
      .join(',\n' + spaces + '  ');
    return `{\n${spaces}  ${props}\n${spaces}}`;
  }

  return '[Unknown]';
};

/**
 * Inspects metadata and returns a string representation
 * @param metadata - The metadata to inspect
 * @returns A string representation of the metadata
 */
const inspectMetadata = (metadata: MetaData): string => format(metadata);

/**
 * Creates and sends a report to Bugsnag with the specified severity and metadata
 * @param msg - The message or Error object to be reported
 * @param severity - The severity level of the error ('info' | 'warning' | 'error')
 * @param metadata - Additional context data for the error
 */
const createBugsnagReport = (msg: string, severity: Severity, metadata: MetaData = {}): void => {
  const error = new Error(msg);
  // Clean stack trace from logger.ts
  error.stack = error.stack
    ?.split('\n')
    .filter((line) => !line.includes('logger.ts'))
    .join('\n');

  bugsnag.notify(error, (report) => {
    report.severity = severity;
    report.addMetadata('extraData', metadata);
  });
};

/**
 * Logger service that provides different logging levels and handles both development
 * and production environments appropriately
 */
const logger = {
  /**
   * Logs informational messages
   * @param msg - The message or Error object to log
   * @param metadata - Additional context data for the log
   */
  info(msg: string, metadata: MetaData = {}): void {
    if (isEnvLocalDev()) {
      console.log(msg, inspectMetadata(metadata));
    } else {
      createBugsnagReport(msg, 'info', metadata);
    }
  },

  /**
   * Logs warning messages
   * @param msg - The message or Error object to log as a warning
   * @param metadata - Additional context data for the warning
   */
  warn(msg: string, metadata: MetaData = {}): void {
    if (isEnvLocalDev()) {
      console.warn(msg, inspectMetadata(metadata));
    } else {
      createBugsnagReport(msg, 'warning', metadata);
    }
  },

  /**
   * Logs error messages
   * @param msg - The message or Error object to log as an error
   * @param metadata - Additional context data for the error
   */
  error(msg: string, metadata: MetaData = {}): void {
    if (isEnvLocalDev()) {
      console.error(msg, inspectMetadata(metadata));
    } else {
      createBugsnagReport(msg, 'error', metadata);
    }
  },

  /**
   * Logs debug messages
   * @param msg - The message or data to log for debugging
   * @param metadata - Additional context data for the debug log
   */
  debug(msg: string, metadata: MetaData = {}): void {
    if (isEnvLocalDev()) {
      console.log('🐞');
      console.log(msg, inspectMetadata(metadata));
    }
  },
};

export { logger };
